<template>
  <section class="mdp" :class="{ 'is-compact': compact }">
    <!-- Top bar: back to the list + position + prev/next -->
    <header class="mdp-top">
      <button type="button" class="mdp-back" @click="$emit('back')">
        <v-icon size="18">mdi-chevron-left</v-icon><span>{{ backLabel }}</span>
      </button>
      <div class="mdp-nav">
        <span class="mdp-count">{{ index + 1 }} of {{ total }}</span>
        <button v-if="mobile" type="button" class="mdp-size" @click="$emit('toggleSize')">
          <v-icon size="16">{{ compact ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          <span>{{ compact ? 'Expand' : 'Collapse' }}</span>
        </button>
      </div>
    </header>

    <div class="mdp-scroll">
      <!-- ── Inline image gallery (no popup) ── -->
      <template v-if="view === 'gallery'">
        <button type="button" class="gallery-back" @click="view = 'detail'">
          <v-icon size="18">mdi-chevron-left</v-icon><span>Back to marker</span>
        </button>
        <div class="gallery-grid">
          <button v-for="(g, i) in galleryThumbs" :key="i" type="button" class="gallery-cell"
            @click="pickGalleryImage(g)">
            <img :src="g" alt="" loading="lazy" />
          </button>
        </div>
      </template>

      <!-- ── Detail ── -->
      <template v-else>
      <!-- Title + status -->
      <div class="mdp-head">
        <h2 class="mdp-title">{{ title }}</h2>
        <span class="mdp-status" :class="statusClass">{{ marker.status }}</span>
      </div>
      <div class="mdp-meta">{{ metaLine }}</div>

      <!-- Tags (shared finding vocabulary) — chips sit up top so categorisation reads at a glance. -->
      <div v-if="!compact && (editingTags || assignedTags.length || canEditTags)" class="mdp-tags">
        <template v-if="!editingTags">
          <div v-if="assignedTags.length" class="mdp-tags-list">
            <TagChip v-for="t in assignedTags" :key="t.id" :name="t.name" :color="t.color" :icon="t.icon" size="sm" />
            <button v-if="canEditTags" type="button" class="mdp-tags-pencil" aria-label="Edit tags" @click="beginEditTags">
              <v-icon size="14">mdi-pencil-outline</v-icon>
            </button>
          </div>
          <button v-else-if="canEditTags" type="button" class="mdp-tags-add" @click="beginEditTags">
            <v-icon size="15">mdi-plus</v-icon><span>Add tags</span>
          </button>
        </template>

        <template v-else>
          <v-select v-model="tagDraft" :items="assignableTags" item-title="name" item-value="id" multiple
            variant="outlined" density="comfortable" hide-details placeholder="Assign tags" class="mdp-tags-select"
            no-data-text="No tags yet — create them in Admin → Tags">
            <template #selection="{ item }">
              <TagChip :name="item.raw.name" :color="item.raw.color" :icon="item.raw.icon" size="sm" class="sel-chip" />
            </template>
          </v-select>
          <div class="mdp-tags-actions">
            <button type="button" class="tags-btn-ghost" :disabled="savingTags" @click="cancelEditTags">Cancel</button>
            <button type="button" class="tags-btn-primary" :disabled="savingTags" @click="saveTags">
              {{ savingTags ? 'Saving…' : 'Save' }}
            </button>
          </div>
        </template>
      </div>

      <!-- Image stays clean — only the AI-trace overlay (staff/AI only, unchanged) sits on it. The
           full-image / gallery / re-capture tools live in a slim toolbar below so they don't crowd the photo. -->
      <div class="mdp-image">
        <img v-if="mainImage" :src="mainImage" class="mdp-image-img" alt="Marker image" @error="useDefaultImage" />
        <div v-else class="mdp-image-ph"><v-progress-circular indeterminate color="#66c7b4" size="28" width="2" /></div>

        <button v-if="showAiTrace && !mobile" type="button" class="img-btn ai-trace" @click="showProvenanceDialog = true">
          <v-icon size="15">mdi-fit-to-page-outline</v-icon><span>AI trace</span>
        </button>
      </div>

      <!-- Image tools — one quiet row. Re-capture re-draws the box in street view to (re)build the
           marker's source image + 360; backfills provenance on legacy markers. -->
      <div v-if="!compact && (provMeta.loading || provMeta.hasProvenance || hasAssociatedFrames || canReimage)"
        class="mdp-imgbar">
        <button v-if="provMeta.loading || provMeta.hasProvenance" type="button" class="mdp-imgbtn"
          title="View the full AI source image"
          :disabled="provMeta.loading || provViewerLoading || !provMeta.hasProvenance"
          @click="openProvenanceImage('source')">
          <v-progress-circular v-if="provMeta.loading" indeterminate size="13" width="2" color="#66c7b4" />
          <v-icon v-else size="15">mdi-image-outline</v-icon><span>Full image</span>
        </button>
        <button v-if="canReimage" type="button" class="mdp-imgbtn"
          title="Re-draw this marker's box in street view to set its source image"
          @click="emit('reimageInStreetView', marker)">
          <!-- eslint-disable-next-line vue/no-v-html -->
          <svg class="mdp-peg" viewBox="0 0 44 68" width="11" height="16" v-html="pegmanSvg"></svg>
          <span>{{ provMeta.hasProvenance ? 'Update image' : 'Add image' }}</span>
        </button>
        <button v-if="hasAssociatedFrames" type="button" class="mdp-imgbtn"
          title="Browse all associated images" @click="openGallery">
          <v-icon size="15">mdi-image-multiple-outline</v-icon><span>All</span>
        </button>
      </div>

      <template v-if="!compact">
      <!-- Actions -->
      <div class="mdp-actions">
        <button type="button" class="mdp-act" @click="$emit('locate', marker)">
          <v-icon size="17">mdi-crosshairs-gps</v-icon><span>Locate</span>
        </button>
        <v-menu location="bottom end" offset="4">
          <template #activator="{ props: menuProps }">
            <button type="button" class="mdp-act mdp-more" v-bind="menuProps" aria-label="More actions">
              <v-icon size="20">mdi-dots-horizontal</v-icon>
            </button>
          </template>
          <v-list class="mdp-menu" density="compact">
            <v-list-item v-if="isPending && can(Permissions.AnomalyReview)" @click="requestApprove">
              <template #prepend><v-icon size="18" color="#4ade80">mdi-check-circle-outline</v-icon></template>
              <v-list-item-title>Accept</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="isPending && can(Permissions.AnomalyReview)" @click="requestReject">
              <template #prepend><v-icon size="18" color="#f87171">mdi-close-circle-outline</v-icon></template>
              <v-list-item-title>Reject</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="showAiTrace && !mobile" @click="showProvenanceDialog = true">
              <template #prepend><v-icon size="18" color="#cfd8dc">mdi-magnify-scan</v-icon></template>
              <v-list-item-title>View AI trace</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="allowTransform && can(Permissions.AnomalyUpdate)" @click="$emit('transformMarker', marker)">
              <template #prepend><v-icon size="18" color="#cfd8dc">mdi-cursor-move</v-icon></template>
              <v-list-item-title>Move</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="can(Permissions.AnomalyDelete)" class="danger-item" @click="showDeleteDialog = true">
              <template #prepend><v-icon size="18" color="#f87171">mdi-delete-outline</v-icon></template>
              <v-list-item-title>Delete</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>

      <!-- Tabs -->
      <div class="mdp-tabs">
        <button type="button" class="mdp-tab" :class="{ active: tab === 'comments' }" @click="tab = 'comments'">
          Comments <span class="tab-count">{{ comments.length }}</span>
        </button>
        <button type="button" class="mdp-tab" :class="{ active: tab === 'activity' }" @click="tab = 'activity'">
          Activity <span class="tab-count">{{ events.length }}</span>
        </button>
      </div>

      <!-- Comments -->
      <div v-if="tab === 'comments'" class="mdp-tabbody">
        <template v-if="comments.length">
          <div v-for="comment in comments" :key="comment.id" class="comment-item">
            <div class="comment-head">
              <strong>{{ comment.createdBy }}</strong>
              <span>{{ timeAgo(comment.createdOn) }}</span>
            </div>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div class="comment-body" v-html="formatCommentBody(comment.comment)"></div>
          </div>
        </template>
        <div v-else class="mdp-empty">
          <p class="empty-title">No comments yet.</p>
          <p class="empty-sub">Add context for whoever reviews this next.</p>
        </div>
      </div>

      <!-- Activity -->
      <div v-else class="mdp-tabbody">
        <template v-if="events.length">
          <div v-for="event in events" :key="event.id" class="event-item">
            <div class="event-head"><strong>{{ event.userName }}</strong><span>{{ timeAgo(event.createdOn) }}</span></div>
            <div class="event-body">{{ event.eventName }}</div>
          </div>
        </template>
        <div v-else class="mdp-empty"><p class="empty-title">No activity yet.</p></div>
      </div>
      </template>
      </template>
    </div>

    <!-- Comment entry -->
    <div v-if="view === 'detail' && !compact && can(Permissions.AnomalyComment)" class="mdp-entry">
      <input v-model="newComment" type="text" class="mdp-input" placeholder="Add a comment"
        @keyup.enter="saveComment" />
      <button type="button" class="mdp-send" :disabled="!newComment" @click="saveComment">
        <v-icon size="18">mdi-send</v-icon>
      </button>
    </div>

    <!-- Delete confirm -->
    <v-dialog v-model="showDeleteDialog" max-width="420">
      <div class="confirm-modal">
        <div class="confirm-icon danger"><v-icon size="26" color="#f87171">mdi-delete-outline</v-icon></div>
        <h2 class="confirm-title">Delete marker?</h2>
        <p class="confirm-text">Are you sure you want to delete this marker?</p>
        <div class="confirm-actions">
          <button type="button" class="btn-ghost" @click="showDeleteDialog = false">Cancel</button>
          <button type="button" class="btn-danger" @click="confirmDeleteYes">Yes, delete</button>
        </div>
      </div>
    </v-dialog>

    <!-- Review confirm -->
    <v-dialog v-model="showReviewDialog" max-width="420">
      <div class="confirm-modal">
        <div class="confirm-icon" :class="pendingReviewAction === 'approve' ? 'success' : 'danger'">
          <v-icon size="26" :color="pendingReviewAction === 'approve' ? '#4ade80' : '#f87171'">
            {{ pendingReviewAction === 'approve' ? 'mdi-check-circle-outline' : 'mdi-close-circle-outline' }}
          </v-icon>
        </div>
        <h2 class="confirm-title">{{ pendingReviewAction === 'approve' ? 'Accept marker?' : 'Reject marker?' }}</h2>
        <p class="confirm-text">
          {{ pendingReviewAction === 'approve' ? 'Mark this anomaly as accepted.' : 'Mark this anomaly as rejected.' }}
        </p>
        <div class="confirm-actions">
          <button type="button" class="btn-ghost" @click="cancelReviewAction">Cancel</button>
          <button type="button" :class="pendingReviewAction === 'approve' ? 'btn-approve' : 'btn-danger'"
            @click="confirmReviewAction">
            {{ pendingReviewAction === 'approve' ? 'Yes, accept' : 'Yes, reject' }}
          </button>
        </div>
      </div>
    </v-dialog>

    <MarkerProvenanceDialog v-if="can(Permissions.DualloAdminView)" v-model="showProvenanceDialog" :marker="marker"
      :scan-id="marker?.scanId" />

    <ProvenanceImageOverlay v-model="provViewerOpen" :title="provViewer.title"
      :source-url="provViewer.sourceUrl" :source-loading="provViewer.sourceLoading"
      :source-file-name="provViewer.sourceFileName" :bbox="provViewer.bbox" :bbox-label="provViewer.label"
      :reference-id="provViewer.referenceId"
      :is360="provViewer.is360" :raw-box="provViewer.rawBox"
      :equirect-url="provViewer.equirectUrl" :equirect-loading="provViewer.equirectLoading"
      :start-mode="provViewer.startMode" @request-equirect="loadEquirectForViewer" />
  </section>
</template>

<script setup>
import { ref, watch, computed, onMounted } from 'vue';
import { useAuth0 } from '@auth0/auth0-vue';
import { format } from 'timeago.js';
import CreateMarkerCommentModel from '@/domain/CreateMarkerCommentModel';
import MarkerComment from '@/domain/MarkerComment';
import MarkerProvenanceDialog from '@/components/MarkerProvenanceDialog.vue';
import ProvenanceImageOverlay from '@/components/ProvenanceImageOverlay.vue';
import MarkerProvenanceService from '@/services/MarkerProvenanceService';
import DualloFramesClient from '@/clients/duallo/DualloFramesClient';
import { buildCubeStrip, buildCubeEquirect, cubeFaceLabelOf, cubeBaseOf, faceFovDegOf, fovDegFromHelper } from '@/lib/cubeStitch';
import { PEGMAN_SVG } from '@/lib/pegmanSvg';
import MarkerService from '@/services/MarkerService.js';
import TagService from '@/services/TagService';
import TagChip from '@/components/tags/TagChip.vue';
import { useOrganizationStore } from '@/store/organizationStore';
import organizationSasService from '@/services/OrganizationSasService';
import { useDefaultImage } from '@/utils/ImageUtils';
import { Permissions } from '@/constants/permissions';
import { usePermissions } from '@/composable/usePermissions';

const props = defineProps({
  marker: { type: Object, required: true },
  assetId: { type: String, required: true },
  scanName: { type: String, default: null },
  areaName: { type: String, default: '' },
  frames: { type: Array, default: () => [] },
  index: { type: Number, default: 0 },
  total: { type: Number, default: 1 },
  // Half-height sheet: show only the image + status, hide tabs/comments/actions.
  compact: { type: Boolean, default: false },
  // Mobile sheet: hide the image-overlay buttons (AI trace / change image).
  mobile: { type: Boolean, default: false },
  // Move/reposition is a precise-pointer action — enabled on desktop only, never on touch.
  allowTransform: { type: Boolean, default: false },
  // Label for the Back button — reflects where this marker was opened from (list, hotspot, zone).
  backLabel: { type: String, default: 'Markers' },
  // Bumped by the parent after the marker's image changes (re-capture) so the panel re-signs the
  // thumbnail even though the marker id is unchanged.
  imageRefresh: { type: Number, default: 0 },
});

const emit = defineEmits([
  'back', 'locate', 'transformMarker', 'toggleSize',
  'onMarkerReviewed', 'onAnomalyDeleted', 'onThumbnailUpdated', 'reimageInStreetView', 'onTagsUpdated',
]);

const { can } = usePermissions();
const { user } = useAuth0();
const organizationStore = useOrganizationStore();

const tab = ref('comments');
const newComment = ref('');
const comments = ref([]);
const events = ref([]);
const imageSrc = ref(null);
const mainImage = ref(null);       // the large image shown (thumbnail, or a chosen gallery image)
const galleryThumbs = ref([]);     // signed urls for the full inline gallery
const view = ref('detail');        // 'detail' | 'gallery' (inline — no popup)
const showDeleteDialog = ref(false);
const showReviewDialog = ref(false);
const showProvenanceDialog = ref(false);
const pendingReviewAction = ref(null);

// Full-image / street-view provenance viewer (same feature as the Findings screen).
const pegmanSvg = PEGMAN_SVG;
const provViewerOpen = ref(false);
const provViewerLoading = ref(false);
const provMeta = ref({ loading: false, hasProvenance: false, is360: false, record: null });
const provViewer = ref({
  title: '', label: '', sourceUrl: null, sourceFileName: null, bbox: null,
  is360: false, equirectUrl: null, equirectLoading: false, base: null, startMode: 'source',
});

async function loadProvMeta() {
  const m = props.marker;
  provMeta.value = { loading: !!m?.id, hasProvenance: false, is360: false, record: null };
  if (!m?.id) return;
  const markerId = m.id;
  try {
    const match = await MarkerProvenanceService.getForMarker(m.scanId, m);
    if (props.marker?.id !== markerId) return;
    const rec = match?.canonical || null;
    provMeta.value = {
      loading: false,
      hasProvenance: !!rec,
      is360: !!rec && /_cube_/i.test(rec.sourceImageFileName || ''),
      record: rec,
    };
  } catch {
    if (props.marker?.id === markerId) provMeta.value = { loading: false, hasProvenance: false, is360: false, record: null };
  }
}

async function openProvenanceImage(startMode = 'source') {
  const m = props.marker;
  if (!m?.id || provViewerLoading.value) return;
  provViewerLoading.value = true;
  try {
    let rec = provMeta.value.record;
    if (!rec) { const match = await MarkerProvenanceService.getForMarker(m.scanId, m); rec = match?.canonical || null; }
    if (!rec) return;
    // A legacy multi-face capture names its source by cube face (..._left.png) and has no stored equirect;
    // modern equirect markers reference the panorama file itself. The former gets stitched into one full
    // panorama below; the latter keeps the existing street-view/equirect tab.
    const faceLabel = cubeFaceLabelOf(rec.sourceImageFileName);
    const isLegacyCube = !!faceLabel;
    const is360 = !isLegacyCube && /_cube_/i.test(rec.sourceImageFileName || '');
    const r = rec.bboxRect;
    // Open the overlay immediately (shimmer), then resolve the image in the background so it feels instant.
    provViewer.value = {
      title: m.name || m.label || 'Marker',
      label: rec.label || m.label || 'Marker',
      referenceId: m.externalReference || null,
      sourceUrl: null,
      sourceLoading: true,
      sourceFileName: rec.sourceImageFileName,
      bbox: { left: r.left, top: r.top, width: r.width, height: r.height },
      rawBox: false,
      is360,
      equirectUrl: null,
      equirectLoading: false,
      base: is360 ? rec.sourceImageFileName.split(/_cube_/i)[0] : null,
      startMode: (startMode === 'equirect' && is360) ? 'equirect' : 'source',
    };
    provViewerOpen.value = true;
    const markerId = m.id;

    if (isLegacyCube) {
      // Stitch the rig's faces into one panorama (equirect from 6 faces, else a 360° side strip) so the
      // full-size view shows the whole detection in context, not a single face. Falls back to the single
      // source face if stitching can't be done.
      const pano = await buildLegacyPanorama(rec, faceLabel, m.scanId).catch(() => null);
      if (!provViewerOpen.value || props.marker?.id !== markerId) return;
      if (pano) {
        provViewer.value = { ...provViewer.value, sourceUrl: pano.url, bbox: pano.box || provViewer.value.bbox, rawBox: true, sourceFileName: pano.base, sourceLoading: false };
      } else {
        const sourceUrl = await MarkerProvenanceService.getSourceImageUrl(rec, {
          scanId: m.scanId, assetId: props.assetId, orgId: organizationStore.organizationId,
        }).catch(() => null);
        if (provViewerOpen.value && props.marker?.id === markerId) provViewer.value = { ...provViewer.value, sourceUrl, sourceLoading: false };
      }
      return;
    }

    const sourceUrl = await MarkerProvenanceService.getSourceImageUrl(rec, {
      scanId: m.scanId, assetId: props.assetId, orgId: organizationStore.organizationId,
    }).catch(() => null);
    // Still showing this marker's image? Attach the resolved URL (or mark it unavailable).
    if (provViewerOpen.value && props.marker?.id === markerId) {
      provViewer.value = { ...provViewer.value, sourceUrl, sourceLoading: false };
    }
  } finally {
    provViewerLoading.value = false;
  }
}

// Stitch a legacy capture's faces into a panorama and map the detection's single-face box onto it:
// an on-the-fly equirect when all 6 faces exist, otherwise a 360° side strip. Returns { url, box, base }
// or null when the rig's frames can't be gathered (caller then falls back to the single source face).
async function buildLegacyPanorama(rec, faceLabel, scanId) {
  const base = cubeBaseOf(rec.sourceImageFileName);
  const [frames, helpersByStem] = await Promise.all([
    MarkerProvenanceService.getScanFrames(scanId),
    MarkerProvenanceService.getCameraHelpersByStem(scanId).catch(() => ({})),
  ]);
  const faces = (frames || [])
    .filter(f => cubeBaseOf(f.fileName) === base && cubeFaceLabelOf(f.fileName))
    // FOV from the camera-helper intrinsics first (filename-independent), falling back to the _fNN_ stamp.
    .map(f => ({ label: cubeFaceLabelOf(f.fileName), url: f.url, fovDeg: fovDegFromHelper(helpersByStem[f.stem]) ?? faceFovDegOf(f.fileName) }));
  const r = rec.bboxRect;
  const faceBox = { left: r.left, top: r.top, width: r.width, height: r.height };
  const has = (l) => faces.some(f => f.label === l);

  // 6 faces → rebuild the equirect (seamless, covers up/down). Needs top AND bottom to be worth it.
  if (has('up') && has('down')) {
    const eq = await buildCubeEquirect(faces).catch(() => null);
    const box = eq && eq.mapBox(faceLabel, faceBox);
    if (eq && box) return { url: eq.dataUrl, box, base };
  }
  // Otherwise a side strip (up/down detections aren't in it → null → fall back to the single face).
  const strip = await buildCubeStrip(faces);
  const box = strip && strip.mapBox(faceLabel, faceBox);
  if (strip && box) return { url: strip.dataUrl, box, base };
  return null;
}

async function loadEquirectForViewer() {
  const v = provViewer.value;
  if (!v.base || v.equirectUrl || v.equirectLoading) return;
  v.equirectLoading = true;
  try {
    const orgId = organizationStore.organizationId;
    const paths = await DualloFramesClient.getEquirectFramePaths(orgId, props.assetId, props.marker.scanId);
    const match = (paths || []).find(p => equirectBaseOf(p) === v.base);
    v.equirectUrl = match ? await organizationSasService.getSignedUrl(match.startsWith('/') ? match : `/${match}`) : null;
  } catch {
    v.equirectUrl = null;
  } finally {
    v.equirectLoading = false;
  }
}

function equirectBaseOf(path) {
  const stem = (path || '').split(/[\\/]/).pop().replace(/\.[^.]+$/, '');
  return stem.replace(/[_-](equirect|cube|pano|360)$/i, '');
}

const title = computed(() => props.marker?.name || props.marker?.label || 'Marker');

const metaLine = computed(() => {
  const parts = [];
  if (props.areaName) parts.push(`[${props.areaName}]`);
  const who = props.marker?.createdBy || props.scanName;
  if (who) parts.push(who);
  if (props.marker?.createdOn) parts.push(`created ${timeAgo(props.marker.createdOn)}`);
  return parts.join(' · ');
});

const statusClass = computed(() => {
  const s = (props.marker?.status || '').toLowerCase();
  if (s === 'approved' || s === 'accepted') return 'is-approved';
  if (s === 'rejected') return 'is-rejected';
  if (s === 'pending') return 'is-pending';
  return 'is-neutral';
});

const isPending = computed(() => (props.marker?.status || '').toLowerCase() === 'pending');

// AI-detection trace is Duallo-staff diagnostic only, and only for AI-generated markers.
const showAiTrace = computed(() => can(Permissions.DualloAdminView) && !!props.marker?.isAIGenerated);
// Re-capture the image in street view, and the free-form image upload, are both Duallo-admin only —
// changing a marker's image can skew reports, so it's staff-gated.
const canReimage = computed(() => can(Permissions.DualloAdminView) && !props.mobile);

const hasAssociatedFrames = computed(() => (props.frames || []).length);

const timeAgo = (dateStr) => format(dateStr);

// ── Tags ──────────────────────────────────────────────────────────────────
// Org-level tags are a shared vocabulary (loaded once); a marker stores the ids it's assigned.
const allTags = ref([]);
const editingTags = ref(false);
const tagDraft = ref([]);
const savingTags = ref(false);
// Local mirror of the marker's assigned tag ids — kept in sync from the prop (watch) and after a save,
// so we never mutate the prop directly (vue/no-mutating-props). Parent owns the store update via emit.
const localTagIds = ref([]);

// Only users who can edit anomalies may (re)assign tags; others see the chips read-only.
const canEditTags = computed(() => can(Permissions.AnomalyUpdate));

const tagsById = computed(() => {
  const map = new Map();
  for (const t of allTags.value) map.set(t.id, t);
  return map;
});

// Chips to display for the current marker — resolve ids to tags, dropping any that no longer exist.
const assignedTags = computed(() =>
  localTagIds.value.map(id => tagsById.value.get(id)).filter(Boolean),
);

// Pickable in edit mode: active tags, plus any already-assigned tag (so a disabled-but-assigned tag
// still shows and can be removed, but disabled tags can't be freshly added once dropped).
const assignableTags = computed(() => {
  const assignedIds = new Set(localTagIds.value);
  return allTags.value.filter(t => t.isActive || assignedIds.has(t.id));
});

async function loadOrgTags() {
  const orgId = organizationStore.organizationId;
  if (!orgId) return;
  allTags.value = await TagService.getTagsByOrganizationId(orgId) || [];
}

function beginEditTags() {
  tagDraft.value = [...localTagIds.value];
  editingTags.value = true;
}

function cancelEditTags() {
  editingTags.value = false;
}

async function saveTags() {
  if (savingTags.value || !props.marker) return;
  savingTags.value = true;
  try {
    const updated = await MarkerService.AssignTags(props.marker.scanId, props.marker.id, tagDraft.value);
    if (updated) {
      // Reflect immediately in this panel, then let the parent sync the marker store.
      localTagIds.value = Array.isArray(updated.tagIds) ? [...updated.tagIds] : [];
      emit('onTagsUpdated', updated);
      editingTags.value = false;
      loadEvents();   // tag changes log a "Tags Updated" activity — refresh so it shows immediately
    }
  } finally {
    savingTags.value = false;
  }
}

onMounted(loadOrgTags);

watch(() => props.marker?.id, async () => {
  if (!props.marker) return;
  imageSrc.value = null;
  mainImage.value = null;
  comments.value = [];
  events.value = [];
  newComment.value = '';
  tab.value = 'comments';
  view.value = 'detail';
  editingTags.value = false;
  localTagIds.value = Array.isArray(props.marker.tagIds) ? [...props.marker.tagIds] : [];
  galleryThumbs.value = [];
  provViewerOpen.value = false;
  loadProvMeta();   // prefetch provenance so the View Full Image / Street view buttons reflect availability
  imageSrc.value = await organizationSasService.getSignedUrl(props.marker.thumbnailPath);
  mainImage.value = imageSrc.value;
  await Promise.all([loadComments(), loadEvents()]);
}, { immediate: true });

// The image changed in place (re-capture) — same marker id, so re-sign the thumbnail (busts the
// cached blob) and reload provenance so View Full Image / Street view reflect the new source.
watch(() => props.imageRefresh, async () => {
  if (!props.marker?.thumbnailPath) return;
  imageSrc.value = await organizationSasService.getSignedUrl(props.marker.thumbnailPath);
  mainImage.value = imageSrc.value;
  loadProvMeta();
  loadEvents();   // the image update logs a "Thumbnail Updated" activity — refresh so it shows without reopening
});

// Open the full inline gallery (signs every frame the first time).
async function openGallery() {
  if (!galleryThumbs.value.length) {
    const list = (props.frames || []).filter(f => f.imagePath);
    galleryThumbs.value = (await Promise.all(
      list.map(f => organizationSasService.getSignedUrl(f.imagePath).catch(() => null)),
    )).filter(Boolean);
  }
  view.value = 'gallery';
}

function pickGalleryImage(url) {
  if (url) mainImage.value = url;
  view.value = 'detail';
}

async function loadComments() {
  const list = await MarkerService.getCommentByMarkerIdAsync(props.marker.id);
  comments.value = (list || []).sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn));
}

async function loadEvents() {
  events.value = await MarkerService.getEventsByMarkerIdAsync(props.marker.id) || [];
}

const formatCommentBody = (body) => (body || '')
  .split(' ')
  .map(w => (w.startsWith('@') ? `<span class="mention-chip">${w}</span>` : w))
  .join(' ');

async function saveComment() {
  if (!newComment.value || !user?.value) return;
  const payload = new CreateMarkerCommentModel(props.marker.id, newComment.value, user.value.name, user.value.sub);
  const response = await MarkerService.createMarkerComment(payload);
  if (response instanceof MarkerComment && response.id) {
    newComment.value = '';
    await loadComments();
  }
}

function requestApprove() { pendingReviewAction.value = 'approve'; showReviewDialog.value = true; }
function requestReject() { pendingReviewAction.value = 'reject'; showReviewDialog.value = true; }
function cancelReviewAction() { showReviewDialog.value = false; pendingReviewAction.value = null; }

async function confirmReviewAction() {
  const marker = props.marker;
  marker.status = pendingReviewAction.value === 'approve' ? 'Approved' : 'Rejected';
  const updated = await MarkerService.UpdateMarkerStatus(marker);
  emit('onMarkerReviewed', updated);
  await loadEvents();
  showReviewDialog.value = false;
  pendingReviewAction.value = null;
}

function confirmDeleteYes() {
  showDeleteDialog.value = false;
  emit('onAnomalyDeleted', props.marker);
}
</script>

<style scoped>
.mdp {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  width: 100%;
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  color: #fff;
  overflow: hidden;
}

/* Top bar */
.mdp-top {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: clamp(9px, calc(6px + 0.25vw), 12px) clamp(10px, calc(6px + 0.3vw), 14px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.mdp-back {
  display: inline-flex; align-items: center; gap: 3px;
  /* p5-4: >=44px touch target via min-height + padding; visible chrome unchanged */
  min-height: 44px;
  background: none; border: none; padding: 4px 8px 4px 4px; border-radius: 8px;
  color: #cfd8dc; font: inherit; font-size: 0.92rem; font-weight: 700; cursor: pointer;
}
.mdp-back:hover { color: #fff; background: rgba(255, 255, 255, 0.05); }
.mdp-nav { display: inline-flex; align-items: center; gap: 6px; }
.mdp-count { font-size: 0.78rem; color: #8aa0aa; font-variant-numeric: tabular-nums; white-space: nowrap; }
/* Mobile collapse/expand toggle — mirrors the markers-list "See all / Collapse". */
.mdp-size {
  display: inline-flex; align-items: center; gap: 2px;
  /* p5-4: >=44px touch target via min-height + padding; visible chrome unchanged */
  min-height: 44px;
  background: none; border: none; padding: 4px 6px 4px 6px; border-radius: 8px;
  color: var(--duallo-accent); font: inherit; font-size: 0.82rem; font-weight: 700; cursor: pointer;
}
.mdp-size:hover { background: rgba(100, 255, 218, 0.1); }

/* Scroll area (themed like the asset list) */
.mdp-scroll {
  flex: 1 1 auto; min-height: 0; overflow-y: auto;
  padding: clamp(12px, calc(8px + 0.3vw), 16px);
  display: flex; flex-direction: column; gap: 14px;
  scrollbar-width: thin; scrollbar-color: #3a4a52 transparent;
}
.mdp-scroll::-webkit-scrollbar { width: 6px; }
.mdp-scroll::-webkit-scrollbar-track { background: transparent; }
.mdp-scroll::-webkit-scrollbar-thumb { background: #3a4a52; border-radius: 3px; }
.mdp-scroll::-webkit-scrollbar-thumb:hover { background: var(--duallo-accent); }

.mdp-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
.mdp-title { font-size: clamp(1.1rem, calc(0.9rem + 0.4vw), 1.3rem); font-weight: 800; color: #fff; margin: 0; min-width: 0; word-break: break-word; }
.mdp-status { flex: 0 0 auto; padding: 3px 11px; border-radius: 999px; font-size: 0.72rem; font-weight: 700; border: 1px solid transparent; }
.mdp-status.is-pending { background: rgba(245, 166, 35, 0.14); color: #f0b968; border-color: rgba(245, 166, 35, 0.4); }
.mdp-status.is-approved { background: rgba(74, 222, 128, 0.14); color: #6ee7a0; border-color: rgba(74, 222, 128, 0.35); }
.mdp-status.is-rejected { background: rgba(248, 113, 113, 0.14); color: #ff9c9c; border-color: rgba(248, 113, 113, 0.4); }
.mdp-status.is-neutral { background: rgba(255, 255, 255, 0.06); color: #cfd8dc; border-color: rgba(255, 255, 255, 0.12); }
.mdp-meta { margin-top: -8px; font-size: 0.8rem; color: #8aa0aa; }

/* Image */
.mdp-image {
  position: relative; width: 100%; aspect-ratio: 16 / 9; border-radius: 12px; overflow: hidden;
  background: #1c272c; border: 1px solid rgba(255, 255, 255, 0.05);
  display: flex; align-items: center; justify-content: center;
}
.mdp-image-img { width: 100%; height: 100%; object-fit: cover; display: block; }
/* Compact (half sheet): the image is the focus — let it fill the remaining height. */
.mdp.is-compact .mdp-image { flex: 1 1 auto; aspect-ratio: auto; min-height: 0; }
.img-btn {
  position: absolute; display: inline-flex; align-items: center; gap: 5px;
  padding: 6px 10px; border-radius: 9px; cursor: pointer;
  background: rgba(10, 15, 18, 0.72); border: 1px solid rgba(255, 255, 255, 0.14); color: #e6edf0;
  font: inherit; font-size: 0.8rem; font-weight: 600; backdrop-filter: blur(4px);
}
.img-btn:hover { background: rgba(10, 15, 18, 0.9); }
.img-btn.ai-trace { left: 10px; bottom: 10px; }
.img-btn.upload { right: 10px; bottom: 10px; padding: 6px; }

/* Image tools — a single slim, quiet row beneath the photo (not overlaid). */
.mdp-imgbar { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
.mdp-imgbtn {
  display: inline-flex; align-items: center; gap: 6px; padding: 6px 10px; border-radius: 9px;
  background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.08); color: #b7c5cc;
  font: inherit; font-size: 0.76rem; font-weight: 600; cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.mdp-imgbtn:hover:not(:disabled) { background: rgba(100, 255, 218, 0.08); border-color: rgba(100, 255, 218, 0.28); color: #64ffda; }
.mdp-imgbtn:disabled { opacity: 0.45; cursor: not-allowed; }

.mdp-peg { display: block; flex: 0 0 auto; }

/* Inline gallery */
.gallery-back {
  display: inline-flex; align-items: center; gap: 4px; margin-bottom: 12px; padding: 6px 12px 6px 8px;
  border-radius: 9px; cursor: pointer; font: inherit; font-size: 0.85rem; font-weight: 700;
  color: #cfd8dc; background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.08);
}
.gallery-back:hover { border-color: rgba(100, 255, 218, 0.4); color: #eef3f2; }
.gallery-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)); gap: 8px;
}
.gallery-cell {
  aspect-ratio: 16 / 10; border-radius: 10px; overflow: hidden; padding: 0; cursor: pointer;
  background: #1c272c; border: 1px solid rgba(255, 255, 255, 0.08);
}
.gallery-cell:hover { border-color: rgba(100, 255, 218, 0.5); }
.gallery-cell img { width: 100%; height: 100%; object-fit: cover; display: block; }

/* Tags — a chip row directly under the title; no section header, just the chips + a small edit affordance. */
.mdp-tags { display: flex; flex-direction: column; gap: 8px; margin-top: -4px; }
.mdp-tags-list { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
.mdp-tags-pencil {
  display: inline-flex; align-items: center; justify-content: center; width: 24px; height: 24px;
  border-radius: 999px; background: none; border: 1px solid rgba(255, 255, 255, 0.16); color: #8aa0aa;
  cursor: pointer; transition: color 0.15s ease, border-color 0.15s ease;
}
.mdp-tags-pencil:hover { color: #64ffda; border-color: rgba(100, 255, 218, 0.4); }
.mdp-tags-add {
  display: inline-flex; align-items: center; gap: 4px; padding: 4px 11px; border-radius: 999px;
  background: rgba(255, 255, 255, 0.04); border: 1px dashed rgba(255, 255, 255, 0.18); color: #b7c5cc;
  font: inherit; font-size: 0.8rem; font-weight: 600; cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}
.mdp-tags-add:hover { border-color: rgba(100, 255, 218, 0.4); color: #64ffda; }
/* Match the panel's native inputs (.mdp-input): flat dark fill, hairline border, teal focus. */
.mdp-tags-select :deep(.v-field) {
  border-radius: 9px; background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1); min-height: 40px;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.mdp-tags-select :deep(.v-field__outline) { display: none; }
.mdp-tags-select :deep(.v-field),
.mdp-tags-select :deep(.v-field__input),
.mdp-tags-select :deep(.v-field__input input),
.mdp-tags-select :deep(.v-field__input input::placeholder) { font-family: 'Inter', sans-serif; }
.mdp-tags-select :deep(.v-field:hover),
.mdp-tags-select :deep(.v-field--focused) { border-color: rgba(100, 255, 218, 0.4); background: rgba(255, 255, 255, 0.06); }
.mdp-tags-select :deep(.v-field__input input::placeholder) { color: #7f939d; opacity: 1; }
.mdp-tags-select :deep(.sel-chip) { margin: 2px 4px 2px 0; }
.mdp-tags-actions { display: flex; justify-content: flex-end; gap: 8px; }
.tags-btn-ghost {
  padding: 7px 14px; border-radius: 9px; background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.12); color: #cfd8dc; font: inherit; font-weight: 600; cursor: pointer;
}
.tags-btn-primary {
  padding: 7px 16px; border-radius: 9px; background: var(--duallo-accent-gradient); color: #071514;
  border: none; font: inherit; font-weight: 700; cursor: pointer;
}
.tags-btn-ghost:disabled, .tags-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

/* Actions */
.mdp-actions { display: flex; gap: 8px; }
.mdp-act {
  flex: 1 1 auto; display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  height: 40px; border-radius: 10px;
  background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.12); color: #e6edf0;
  font: inherit; font-size: 0.88rem; font-weight: 600; cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.mdp-act:hover:not(:disabled) { background: rgba(100, 255, 218, 0.08); border-color: rgba(100, 255, 218, 0.4); color: #fff; }
.mdp-act:disabled { opacity: 0.45; cursor: not-allowed; }
.mdp-more { flex: 0 0 auto; width: 44px; }

/* Tabs */
.mdp-tabs { display: flex; gap: 16px; border-bottom: 1px solid rgba(255, 255, 255, 0.08); }
.mdp-tab {
  display: inline-flex; align-items: center; gap: 6px; padding: 8px 2px; margin-bottom: -1px;
  background: none; border: none; border-bottom: 2px solid transparent;
  color: #8aa0aa; font: inherit; font-size: 0.88rem; font-weight: 600; cursor: pointer;
}
.mdp-tab.active { color: #fff; border-bottom-color: var(--duallo-accent); }
.tab-count { font-size: 0.74rem; color: #667a83; }
.mdp-tab.active .tab-count { color: #8aa0aa; }

.mdp-tabbody { display: flex; flex-direction: column; gap: 8px; }
.mdp-empty { text-align: center; padding: 26px 10px; color: #8aa0aa; }
.empty-title { margin: 0; font-size: 0.92rem; color: #cfd8dc; }
.empty-sub { margin: 4px 0 0; font-size: 0.84rem; }

.comment-item, .event-item {
  background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 10px; padding: 10px 12px;
}
.comment-head, .event-head {
  display: flex; justify-content: space-between; gap: 10px; margin-bottom: 5px; font-size: 0.78rem; color: #8aa0aa;
}
.comment-head strong, .event-head strong { color: #cfd8dc; font-weight: 600; }
.comment-body, .event-body { color: #e6edf0; font-size: 0.88rem; line-height: 1.5; word-break: break-word; }
:deep(.mention-chip) {
  display: inline-block; padding: 1px 8px; border-radius: 999px;
  background: rgba(100, 255, 218, 0.15); border: 1px solid rgba(100, 255, 218, 0.3); color: var(--duallo-accent); font-weight: 600;
}

/* Comment entry */
.mdp-entry {
  flex: 0 0 auto; display: flex; align-items: center; gap: 8px;
  padding: 10px 12px; border-top: 1px solid rgba(255, 255, 255, 0.06);
}
.mdp-input {
  flex: 1 1 auto; min-width: 0; height: 40px; padding: 0 12px; border-radius: 9px;
  background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.1); color: #e6edf0; font: inherit; outline: none;
}
.mdp-input::placeholder { color: #7f939d; }
.mdp-input:focus { border-color: rgba(100, 255, 218, 0.4); background: rgba(255, 255, 255, 0.06); }
.mdp-send {
  flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center;
  width: 40px; height: 40px; border-radius: 9px;
  background: var(--duallo-accent-gradient); color: #071514; border: none; cursor: pointer;
}
.mdp-send:disabled { opacity: 0.4; cursor: not-allowed; }

/* Confirm modals */
.confirm-modal {
  background: var(--duallo-card-gradient); border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px; box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45); color: #fff; padding: 26px 22px 20px; text-align: center;
}
.confirm-icon { width: 52px; height: 52px; margin: 0 auto 12px; border-radius: 14px; display: flex; align-items: center; justify-content: center; }
.confirm-icon.danger { background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.25); }
.confirm-icon.success { background: rgba(74, 222, 128, 0.1); border: 1px solid rgba(74, 222, 128, 0.25); }
.confirm-title { font-size: 1.2rem; font-weight: 700; margin: 0 0 6px; }
.confirm-text { font-size: 0.92rem; color: #b0bec5; margin: 0 0 20px; line-height: 1.5; }
.confirm-actions { display: flex; gap: 10px; justify-content: center; }
.confirm-actions > * { flex: 1 1 0; max-width: 160px; }
.btn-ghost { padding: 10px 18px; border-radius: 10px; background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.12); color: #cfd8dc; font: inherit; font-weight: 600; cursor: pointer; }
.btn-danger { padding: 10px 18px; border-radius: 10px; background: rgba(239, 68, 68, 0.15); border: 1px solid rgba(239, 68, 68, 0.4); color: #f87171; font: inherit; font-weight: 700; cursor: pointer; }
.btn-approve { padding: 10px 18px; border-radius: 10px; background: rgba(74, 222, 128, 0.15); border: 1px solid rgba(74, 222, 128, 0.4); color: #4ade80; font: inherit; font-weight: 700; cursor: pointer; }

.mdp-menu { background: #1f2c34 !important; border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 10px; }
.danger-item :deep(.v-list-item-title) { color: #ff9c9c; }
</style>
